import React, { useState } from 'react';
import type { DropdownQuestion as DropdownQuestionType } from './survey.types';
import { useLanguageStore } from '../../stores/languageStore';
import LanguageHelper from '../../utils/languageHelper';

interface DropdownQuestionProps {
  question: DropdownQuestionType;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  displayNumber?: number;
}

export const DropdownQuestion: React.FC<DropdownQuestionProps> = ({
  question,
  value,
  onChange,
  error,
  displayNumber,
}) => {
  const languageCode = useLanguageStore((state) => state.code);
  const [otherText, setOtherText] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(() => {
    return value?.startsWith('other:') || false;
  });

  React.useEffect(() => {
    if (value?.startsWith('other:')) {
      const text = value.substring('other:'.length);
      setShowOtherInput(true);
      setOtherText(prevText => prevText === text ? prevText : text);
    } else if (!value) {
      setShowOtherInput(false);
      setOtherText('');
    }
  }, [value]);

  const handleSelectChange = (selectedValue: string) => {
    if (selectedValue === 'other') {
      setShowOtherInput(true);
      onChange(`other:${otherText}`);
    } else if (selectedValue === '') {
      setShowOtherInput(false);
      setOtherText('');
      onChange('');
    } else {
      setShowOtherInput(false);
      setOtherText('');
      onChange(selectedValue);
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    onChange(`other:${text}`);
  };

  const selectLabel = LanguageHelper.getExtendedTranslation(languageCode, 'selectOption');
  const specifyText = LanguageHelper.getExtendedTranslation(languageCode, 'pleaseSpecify');

  // Get current selection value for the dropdown
  const currentSelectValue = (() => {
    if (!value) return '';
    if (value.startsWith('other:')) return 'other';
    return value;
  })();

  return (
    <div className="survey-question dropdown-question">
      <label className="question-label">
        {displayNumber != null && (
          <span className="question-number">{displayNumber}.</span>
        )}
        <span dangerouslySetInnerHTML={{ __html: question.question_text[languageCode] }} />
        {question.is_required && <span className="required-indicator">*</span>}
      </label>
      <div className="dropdown-container">
        <select
          value={currentSelectValue}
          onChange={(e) => handleSelectChange(e.target.value)}
          className={`dropdown-select ${error ? 'has-error' : ''}`}
        >
          <option value="">{selectLabel}</option>
          {question.choices.map((choice) => (
            <option key={choice.choice_number} value={`choice_${choice.choice_number}`}>
              {choice.choice_text[languageCode]}
            </option>
          ))}
          {question.showOther && (
            <option value="other">
              {question.otherValue?.[languageCode] || LanguageHelper.getTranslations(languageCode).other}
            </option>
          )}
        </select>
        {showOtherInput && (
          <div className="other-input-block">
            <input
              type="text"
              value={otherText}
              onChange={(e) => handleOtherTextChange(e.target.value)}
              placeholder={specifyText}
              className={`other-input ${error ? 'has-error' : ''}`}
            />
          </div>
        )}
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};
